import { Injectable, OnDestroy } from '@angular/core'
import {
  Address,
  AddressService as AddressApiService,
} from '@sciper/api-client'
import { BehaviorSubject, Subject, takeUntil } from 'rxjs'
import {
  AbstractRestService,
  RestServiceStatus,
} from '../abstract-rest/abstract-rest.service'

export type AddressPageOptions = {
  page: number
  size: number
}

export const DEFAULT_PAGE_SIZE = 20

@Injectable({
  providedIn: 'root',
})
export class AddressService extends AbstractRestService implements OnDestroy {
  private _destroySubject$: Subject<void> = new Subject<void>()

  address: BehaviorSubject<Address> = new BehaviorSubject(undefined)
  addresses: BehaviorSubject<Address[]> = new BehaviorSubject([])

  constructor(private addressApiService: AddressApiService) {
    super()
  }

  override ngOnDestroy() {
    super.ngOnDestroy()
    this._destroySubject$.next()
    this._destroySubject$.complete()
  }

  fetchAddress(addressId: string) {
    this.status.next(RestServiceStatus.Loading)
    this.addressApiService
      .getAddress(addressId)
      .pipe(takeUntil(this._destroySubject$))
      .subscribe(
        (address) => {
          this.address.next(address)
          this.status.next(RestServiceStatus.Success)
        },
        (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
          } else {
            this.status.next(RestServiceStatus.Error)
          }
        }
      )
  }

  fetchAddressList(
    searchKey: string | undefined = undefined,
    pageOptions: AddressPageOptions = {
      page: 0,
      size: DEFAULT_PAGE_SIZE,
    }
  ) {
    this.status.next(RestServiceStatus.Loading)
    this.addressApiService
      .getAddressList(searchKey, pageOptions.page, pageOptions.size)
      .pipe(takeUntil(this._destroySubject$))
      .subscribe(
        (addressList) => {
          const currentAddresses = this.addresses.value ?? []
          if (currentAddresses.length < DEFAULT_PAGE_SIZE) {
            // replace previous addresses due to reloading page 0
            this.addresses.next(addressList.items)
          } else {
            // append to previous addresses
            const newAddresses = addressList.items.filter(
              (newAddress) =>
                currentAddresses.findIndex(
                  (address) => address.addressId === newAddress.addressId
                ) < 0
            )
            currentAddresses.push(...newAddresses)
            this.addresses.next(currentAddresses)
          }

          this.status.next(RestServiceStatus.Success)
        },
        (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
          } else {
            this.status.next(RestServiceStatus.Error)
          }
        }
      )
  }

  createAddress(
    address: Address,
    callback:
      | ((success: boolean, addressId: string | undefined) => void)
      | undefined = undefined
  ) {
    this.status.next(RestServiceStatus.Loading)
    this.addressApiService
      .createAddress(address)
      .pipe(takeUntil(this._destroySubject$))
      .subscribe(
        (addressId: string) => {
          this.status.next(RestServiceStatus.Success)
          if (callback) {
            callback(true, addressId)
          }
        },
        (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
          } else {
            this.status.next(RestServiceStatus.Error)
          }
          if (callback) {
            callback(false, undefined)
          }
        }
      )
  }

  updateAddress(
    addressId: string,
    address: Address,
    callback: ((success: boolean) => void) | undefined = undefined
  ) {
    this.status.next(RestServiceStatus.Loading)
    this.addressApiService
      .updateAddress(addressId, address)
      .pipe(takeUntil(this._destroySubject$))
      .subscribe(
        (_) => {
          this.status.next(RestServiceStatus.Success)
          if (callback) {
            callback(true)
          }
        },
        (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
          } else {
            this.status.next(RestServiceStatus.Error)
          }
          if (callback) {
            callback(false)
          }
        }
      )
  }

  deleteAddress(
    addressId: string,
    callback: ((success: boolean) => void) | undefined = undefined
  ) {
    this.status.next(RestServiceStatus.Loading)
    this.addressApiService
      .deleteAddress(addressId)
      .pipe(takeUntil(this._destroySubject$))
      .subscribe(
        (_) => {
          this.status.next(RestServiceStatus.Success)
          if (callback) {
            callback(true)
          }
        },
        (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
          } else {
            this.status.next(RestServiceStatus.Error)
          }
          if (callback) {
            callback(false)
          }
        }
      )
  }
}
